import { useState } from 'react'

import AddIcon from '@mui/icons-material/Add'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import DeleteIcon from '@mui/icons-material/Delete'
import IconButton from '@mui/material/IconButton'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import StyledTable from '@components/StyledTable'
import type { Entity } from '@models/Entity'
import { formatDateTime } from '@util/date-util'

import { StyledExpandCard } from '../../../theme/components/StyledExpandCard'
import { StyledIconButton } from '../../../theme/components/StyledIconButton'
import { useOpenBijlageForEntity } from '../hooks/useOpenBijlageForEntity'
import type { Bijlage } from '../types/Bijlage'

import { BijlagenDialog } from './BijlagenDialog'

const headerCells = [
  { id: 'naam', numeric: true, label: 'Naam', sort: false },
  { id: 'created.date', label: 'Aangemaakt', fixed: 50 },
  { id: 'created.organisatie', label: 'Aangemaakt door', fixed: 150 },
  { id: 'opties', label: 'Opties', sort: false, fixed: 100 },
]

const createNaamByStatus = ({ naam, status }) => {
  if (status === 'cancelled') return `${naam} - geannuleerd`
  if (status === 'rejected') return `${naam} - afgekeurd`
  if (status === 'accepted') return `${naam} - goedgekeurd`

  return naam
}

type Props = {
  entity: Entity
  bijlagen: Bijlage[]
  showUploadButton?: boolean
}

export const Bijlagen = ({
  entity,
  bijlagen,
  showUploadButton = true,
}: Props) => {
  const { mutate: openBijlageForEntity } = useOpenBijlageForEntity()
  const [type, setType] = useState<'upload' | 'cancel' | undefined>(undefined)
  const [selectedBijlage, setSelectedBijlage] = useState<Bijlage | undefined>(
    undefined,
  )

  const handleUpload = () => {
    setType('upload')
  }

  const handleCancel = (bijlage: Bijlage) => {
    setSelectedBijlage(bijlage)
    setType('cancel')
  }

  const handleOpen = (bijlage: Bijlage) => {
    openBijlageForEntity({ entity, bijlage })
  }

  const handleClose = () => {
    setType(undefined)
    setSelectedBijlage(undefined)
  }

  return (
    <StyledExpandCard
      title="Bijlagen"
      buttonBar={
        <>
          {showUploadButton && (
            <StyledIconButton
              title="Bijlage toevoegen"
              onClick={handleUpload}
              icon={<AddIcon />}
            />
          )}
        </>
      }
    >
      <StyledTable variant="details" headerCells={headerCells} {...({} as any)}>
        {bijlagen.map((row) => (
          <TableRow key={row._id}>
            <TableCell>
              {createNaamByStatus(row)} <br />
              <Typography variant="caption">{row.omschrijving}</Typography>
            </TableCell>
            <TableCell>{formatDateTime(row.created.date)}</TableCell>
            <TableCell>{row.created.organisatie}</TableCell>
            <TableCell>
              <Tooltip title="Bijlage openen">
                <IconButton onClick={() => handleOpen(row)}>
                  <CloudDownloadIcon />
                </IconButton>
              </Tooltip>
              {row.status !== 'cancelled' && (
                <Tooltip title="Bijlage annuleren">
                  <IconButton onClick={() => handleCancel(row)}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              )}
            </TableCell>
          </TableRow>
        ))}
      </StyledTable>

      <BijlagenDialog
        entity={entity}
        type={type}
        bijlage={selectedBijlage}
        onClose={handleClose}
        assignees={[]}
        bijlageTemplates={[]}
        disciplineVerzamelingen={[]}
        processtatus=""
      />
    </StyledExpandCard>
  )
}
